'use client';

import React, { useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/solid';

interface EnterpriseWaitlistModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface FormData {
  agencyName: string;
  primaryEmail: string;
  primaryPhone: string;
  totalAgentCount: string;
}

interface FormErrors {
  agencyName?: string;
  primaryEmail?: string;
  totalAgentCount?: string;
}

const agentCountOptions = [
  '1-5 Agents',
  '6-10 Agents',
  '11-25 Agents',
  '26-50 Agents',
  '51-100 Agents',
  '100+ Agents',
];

export default function EnterpriseWaitlistModal({ isOpen, onClose }: EnterpriseWaitlistModalProps) {
  const [formData, setFormData] = useState<FormData>({
    agencyName: '',
    primaryEmail: '',
    primaryPhone: '',
    totalAgentCount: '',
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.agencyName.trim()) {
      newErrors.agencyName = 'Agency name is required';
    }

    if (!formData.primaryEmail.trim()) {
      newErrors.primaryEmail = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.primaryEmail)) {
      newErrors.primaryEmail = 'Invalid email format';
    }

    if (!formData.totalAgentCount) {
      newErrors.totalAgentCount = 'Please select agent count';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const res = await fetch('/api/enterprise-inquiry', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          agencyName: formData.agencyName,
          primaryEmail: formData.primaryEmail,
          primaryPhone: formData.primaryPhone,
          totalAgentCount: formData.totalAgentCount,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setSubmitError(data.error || 'Failed to submit. Please try again.');
        setIsSubmitting(false);
        return;
      }

      setSubmitSuccess(true);
      setFormData({ agencyName: '', primaryEmail: '', primaryPhone: '', totalAgentCount: '' });
      setErrors({});

      setTimeout(() => {
        setSubmitSuccess(false);
        onClose();
      }, 2000);
    } catch (err) {
      setSubmitError('An unexpected error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div
        className="relative bg-card border border-border shadow-elevation-3 w-full max-w-md mx-4"
        style={{ borderRadius: '6px' }}
      >
        {/* Header */}
        <div className="bg-[#236434] px-6 py-4 flex items-center justify-between" style={{ borderRadius: '6px 6px 0 0' }}>
          <h2 className="text-xl font-medium text-white" style={{ fontFamily: 'Poppins, sans-serif', fontWeight: 500 }}>
            Enterprise Waitlist
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white transition-colors"
            aria-label="Close modal"
          >
            <XMarkIcon className="w-6 h-6" style={{ strokeWidth: '1.5px', shapeRendering: 'geometricPrecision' }} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Agency Name */}
          <div>
            <label htmlFor="agencyName" className="block text-sm font-medium text-foreground mb-2">
              Agency Name
            </label>
            <input
              type="text"
              id="agencyName"
              value={formData.agencyName}
              onChange={(e) => handleInputChange('agencyName', e.target.value)}
              className={`w-full px-4 py-2.5 bg-background text-foreground border ${
                errors.agencyName ? 'border-[#ff731a]' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-[#18a5ff] transition-all`}
              style={{ borderRadius: '6px' }}
              placeholder="Enter your agency name"
            />
            {errors.agencyName && (
              <p className="text-[#ff731a] text-sm mt-1">{errors.agencyName}</p>
            )}
          </div>

          {/* Primary Phone */}
          <div>
            <label htmlFor="primaryPhone" className="block text-sm font-medium text-foreground mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              id="primaryPhone"
              value={formData.primaryPhone}
              onChange={(e) => handleInputChange('primaryPhone', e.target.value)}
              className="w-full px-4 py-2.5 bg-background text-foreground border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#18a5ff] transition-all"
              style={{ borderRadius: '6px' }}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Primary Email */}
          <div>
            <label htmlFor="primaryEmail" className="block text-sm font-medium text-foreground mb-2">
              Primary Email
            </label>
            <input
              type="email"
              id="primaryEmail"
              value={formData.primaryEmail}
              onChange={(e) => handleInputChange('primaryEmail', e.target.value)}
              className={`w-full px-4 py-2.5 bg-background text-foreground border ${
                errors.primaryEmail ? 'border-[#ff731a]' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-[#18a5ff] transition-all`}
              style={{ borderRadius: '6px' }}
              placeholder="your@email.com"
            />
            {errors.primaryEmail && (
              <p className="text-[#ff731a] text-sm mt-1">{errors.primaryEmail}</p>
            )}
          </div>

          {/* Total Agent Count */}
          <div>
            <label htmlFor="totalAgentCount" className="block text-sm font-medium text-foreground mb-2">
              Total Agent Count
            </label>
            <select
              id="totalAgentCount"
              value={formData.totalAgentCount}
              onChange={(e) => handleInputChange('totalAgentCount', e.target.value)}
              className={`w-full px-4 py-2.5 bg-background text-foreground border ${
                errors.totalAgentCount ? 'border-[#ff731a]' : 'border-gray-300'
              } focus:outline-none focus:ring-2 focus:ring-[#18a5ff] transition-all`}
              style={{ borderRadius: '6px' }}
            >
              <option value="">Select agent count</option>
              {agentCountOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            {errors.totalAgentCount && (
              <p className="text-[#ff731a] text-sm mt-1">{errors.totalAgentCount}</p>
            )}
          </div>

          {/* Submit Error */}
          {submitError && (
            <div className="bg-[#ff731a]/10 border border-[#ff731a] px-4 py-3" style={{ borderRadius: '6px' }}>
              <p className="text-[#ff731a] text-sm">{submitError}</p>
            </div>
          )}

          {/* Submit Success */}
          {submitSuccess && (
            <div className="bg-[#12d486]/10 border border-[#12d486] px-4 py-3" style={{ borderRadius: '6px' }}>
              <p className="text-[#12d486] text-sm font-medium">Successfully joined the waitlist! We'll be in touch soon.</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isSubmitting || submitSuccess}
            className="w-full bg-[#236434] text-white py-3 font-semibold hover:bg-[#236434]/90 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            style={{ borderRadius: '6px' }}
          >
            {isSubmitting ? 'Submitting...' : submitSuccess ? 'Submitted!' : 'Submit'}
          </button>
        </form>

        {/* Footer with TallyMedicare Logo */}
        <div className="px-6 pb-6 pt-2 border-t border-border">
          <div className="flex items-center justify-center gap-2">
            <span
              className="text-[#12d486]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 900,
                fontSize: '1.15rem',
                letterSpacing: '-0.02em',
              }}
            >
              Tally
            </span>
            <span
              className="text-[#12d486]"
              style={{
                fontFamily: 'Poppins, sans-serif',
                fontWeight: 500,
                fontSize: '1.15rem',
                letterSpacing: '-0.01em',
              }}
            >
              Medicare
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
